'use server'

import {
    PaymentQuoteOption,
    QuoteMethod,
} from '@/lib/api/financial/financial-billing-api'
import { apiRequest, ApiResponse } from '../request'

const BASE = 'financial/sale-simulator'

/**
 * O mesmo cálculo de `financial/billings/payment-options`, sob a permissão do
 * simulador.
 *
 * A rota é separada porque o vendedor não tem acesso a cobranças: usar a rota do
 * billing faria a tela abrir e a cotação voltar 403, sem nada na tela explicando.
 */
export async function getSaleSimulatorOptions(
    amount: number,
    methods?: QuoteMethod[]
): Promise<ApiResponse<{ baseAmount: number; options: PaymentQuoteOption[] }>> {
    return apiRequest(`${BASE}/payment-options`, {
        params: {
            amount,
            ...(methods?.length ? { methods: methods.join(',') } : {}),
        },
    })
}

export interface SaleSimulationPdfInput {
    /** Valor da venda em centavos. */
    amount: number
    /** Título escrito pelo operador. Vazio imprime o documento sem título. */
    title?: string
    /** Forma combinada, destacada no documento. */
    method?: QuoteMethod
    installments?: number
    /**
     * Sem estes dois, o documento leva só a condição combinada — o filtro é
     * aplicado no servidor, junto do recálculo.
     */
    showAllInstallments?: boolean
    showAllMethods?: boolean
    /** Cliente do cadastro: o nome impresso vem do registro, não daqui. */
    clientId?: number
    notes?: string
}

/**
 * PDF da simulação, por blob.
 *
 * Blob e não `<a href>`: a rota exige o JWT do cliente HTTP, e um link direto para
 * a API sairia sem token e responderia 401.
 */
async function fetchSimulationPdf(
    input: SaleSimulationPdfInput
): Promise<{ blob: Blob; fileName: string }> {
    const { getClient } = await import('@/lib/api/client')
    const client = await getClient()
    const response = await client.post(`${BASE}/pdf`, input, {
        responseType: 'blob',
    })

    // O nome do arquivo é derivado do título NO SERVIDOR (ver `fileNameFromTitle`):
    // repetir a regra aqui faria o arquivo baixado divergir do que a API nomeou.
    const disposition = String(
        response.headers?.['content-disposition'] ?? ''
    )
    const fileName =
        /filename="([^"]+)"/.exec(disposition)?.[1] || 'documento.pdf'

    return {
        blob: new Blob([response.data], { type: 'application/pdf' }),
        fileName,
    }
}

/** URL temporária, para abrir no viewer do navegador e imprimir de lá. */
export async function getSaleSimulationPdfUrl(
    input: SaleSimulationPdfInput
): Promise<string> {
    const { blob } = await fetchSimulationPdf(input)
    return URL.createObjectURL(blob)
}

/** Baixa o documento com o nome que o servidor deu (o título do operador). */
export async function downloadSaleSimulationPdf(
    input: SaleSimulationPdfInput
): Promise<void> {
    const { downloadBlob } = await import('@/lib/download-blob')
    const { blob, fileName } = await fetchSimulationPdf(input)
    downloadBlob(blob, fileName)
}
